/**
 * Uma reunião no console — a tela 7: o material, a ata e a fronteira do que sai daqui.
 *
 * **Três blocos, nesta ordem, e a ordem é o argumento.** Primeiro a reunião (quando foi, se
 * aconteceu); depois o material (a transcrição pronta, que é o caminho que funciona hoje); por
 * último a ata, que é o que o cliente lê. Inverter faria a tela começar pelo pipeline que não
 * roda — e uma tela que abre com uma barra de progresso parada promete processamento.
 *
 * **O material é interno, a ata é a fronteira.** A gravação e a transcrição exigem
 * `ata:ver_interno`: elas são a conversa inteira do cliente e a matéria-prima de onde as notas
 * internas foram extraídas. O que atravessa é a ata publicada, item a item, e cada item diz para
 * onde vai antes de alguém precisar procurar.
 *
 * **A ata que esta tela mostra já vem recortada.** `GET /api/reunioes/{id}` devolve a ata do
 * público de quem pediu — um interno sem `ata:ver_interno` lê a mesma ata com as notas internas
 * ausentes, e o contador `ocultos` diz quantas são. A tela não filtra nada: filtrar aqui criaria
 * um segundo lugar para a mesma decisão, e o segundo é o que esquece a fronteira num campo
 * derivado.
 */

import { Link, useParams } from "react-router";
import { Aviso, Cartao, Chip } from "../componentes/ui";
import { AcoesDaReuniao, EditorDaAta, GravacaoDaReuniao } from "../componentes/reuniao";
import { useTitulo } from "../nucleo/titulo";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { mensagemDoConsole, useEsteiraDaImplantacao } from "../nucleo/console";
import {
  PERMISSAO_DE_AGENDAR,
  PERMISSAO_DE_EDITAR_ATA,
  PERMISSAO_DE_PUBLICAR_ATA,
  PERMISSAO_DE_VER_NOTA_INTERNA,
  caminhoDasReunioes,
  formatarDuracao,
  formatarQuando,
  formatarQuandoPorExtenso,
  rotuloDoEstadoDaReuniao,
  rotuloDoTipoDeReuniao,
  useMidias,
  useReuniao,
  type Midia,
} from "../nucleo/reuniao";

export function ReuniaoNoConsole() {
  const { id, reuniao: reuniaoId } = useParams();
  const implantacaoId = id ?? "";
  const sessao = useSessao();
  const reuniao = useReuniao(reuniaoId ?? "");
  const veNotaInterna = podeNaSessao(sessao.data, PERMISSAO_DE_VER_NOTA_INTERNA);
  const podeEditar = podeNaSessao(sessao.data, PERMISSAO_DE_EDITAR_ATA);
  const podePublicar = podeNaSessao(sessao.data, PERMISSAO_DE_PUBLICAR_ATA);
  const podeAgendar = podeNaSessao(sessao.data, PERMISSAO_DE_AGENDAR);
  /* A mesma listagem que o bloco de gravação já pede: o react-query resolve as duas na mesma
     chave, e o editor precisa dela para oferecer a transcrição de origem. Pedir de novo aqui não
     gera segunda ida ao servidor, e evita passar a lista de mão em mão por três componentes. */
  const midias = useMidias(reuniaoId ?? "", veNotaInterna);
  /* O nome do cliente vem da MESMA consulta que a tela da implantação usa (o react-query a
     resolve na mesma chave), e ele existe aqui por um motivo só: a confirmação de publicar diz
     para quem a ata vai. Publicar a ata do cliente errado é caro e silencioso. */
  const esteira = useEsteiraDaImplantacao(implantacaoId);

  useTitulo("Reunião");

  if (reuniao.isPending) {
    return (
      <p className="lead" role="status">
        Carregando a reunião…
      </p>
    );
  }

  if (reuniao.isError) {
    return (
      <>
        <h1 className="titulo-pagina">Reunião</h1>
        <Aviso tom="erro" data-testid="falha-da-reuniao">
          {mensagemDoConsole(reuniao.error)}
        </Aviso>
        <p className="nota-rodape mt-4">
          <Link to={caminhoDasReunioes(implantacaoId)}>Voltar às reuniões</Link>
        </p>
      </>
    );
  }

  const dados = reuniao.data;
  const transcricoes: readonly Midia[] = (midias.data?.midias ?? []).filter(
    (midia) => midia.tipo === "transcricao" && midia.estado === "concluida",
  );

  const cliente =
    esteira.data?.organizacao.nome_fantasia ??
    esteira.data?.organizacao.razao_social ??
    "este cliente";

  return (
    <div data-testid="detalhe-da-reuniao">
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">{rotuloDoTipoDeReuniao(dados.tipo)}</h1>
          <p className="reuniao__quando">
            {formatarQuandoPorExtenso(dados.agendada_para) ?? "—"} ·{" "}
            {formatarDuracao(dados.duracao_min)}
          </p>
          <p className="reuniao__meta">
            {dados.link ? (
              <a href={dados.link} target="_blank" rel="noreferrer noopener">
                {dados.link}
              </a>
            ) : (
              "sem link de sala registrado"
            )}
            {dados.agendada_por ? <> · marcada por {dados.agendada_por.nome}</> : null}
            {dados.realizada_em ? (
              <> · realizada em {formatarQuando(dados.realizada_em)}</>
            ) : null}
          </p>
        </div>
        <Chip
          estado={
            dados.estado === "realizada"
              ? "ok"
              : dados.estado === "cancelada"
                ? "neutro"
                : "aguardando"
          }
          data-testid="estado-da-reuniao"
        >
          {rotuloDoEstadoDaReuniao(dados.estado)}
        </Chip>
      </div>

      <section className="mb-6" aria-labelledby="titulo-da-reuniao">
        <h2 className="titulo-secao" id="titulo-da-reuniao">
          A reunião
        </h2>
        <AcoesDaReuniao reuniao={dados} podeAgendar={podeAgendar} />
        {dados.evento_externo_id === null ? (
          <p className="nota-rodape mt-3">
            Reunião registrada no modo <b>manual</b>: o portal guarda o horário e o link que você
            marcou no seu calendário. Ele não cria evento nem sala — não há service account com
            delegação de domínio na organização, e o adaptador de calendário está declarado e sem
            implementação.
          </p>
        ) : null}
      </section>

      <section className="mb-6" aria-labelledby="titulo-do-material">
        <h2 className="titulo-secao" id="titulo-do-material">
          Gravação e transcrição
        </h2>
        <GravacaoDaReuniao
          reuniaoId={dados.id}
          veNotaInterna={veNotaInterna}
          podeEditar={podeEditar}
        />
      </section>

      <section className="mb-6" aria-labelledby="titulo-da-ata">
        <h2 className="titulo-secao" id="titulo-da-ata">
          Ata
        </h2>
        {!veNotaInterna && dados.ata !== null ? (
          <Cartao className="mb-3" titulo="Você está lendo a ata sem as notas internas">
            <p className="cartao__texto">
              O seu acesso não mostra as notas internas. O que falta na lista abaixo está contado
              como <b>itens ocultos</b> — a ata que você lê é verdadeira, só não é completa.
            </p>
          </Cartao>
        ) : null}
        <EditorDaAta
          reuniaoId={dados.id}
          tipo={dados.tipo}
          cliente={cliente}
          ata={dados.ata}
          transcricoes={transcricoes}
          podeEditar={podeEditar && dados.estado !== "cancelada"}
          podePublicar={podePublicar}
          veNotaInterna={veNotaInterna}
          reuniaoRealizada={dados.estado === "realizada"}
        />
      </section>

      <p className="nota-rodape mt-5">
        <Link to={caminhoDasReunioes(implantacaoId)}>Voltar às reuniões</Link>
      </p>
    </div>
  );
}
